// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Image, Platform, Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import GameWeightControl from '@/components/GameWeightControl';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { t } from '@/i18n';
import appColors from '@/theme/colors';
import type { GameDefinition } from '../../types';
import { resolveGameImageSource } from './gameImage';

type Props = {
  game: GameDefinition;
  selected: boolean;
  locked?: boolean;
  lockedLabel?: string;
  onPress: () => void;
  onInfoPress: () => void;
  meritLadderLabel?: string;
  perTeam?: boolean;
  weightPercent?: number;
  onWeightPress?: () => void;
  weightLocked?: boolean;
};

export default function GameTile({
  game,
  selected,
  locked = false,
  lockedLabel,
  onPress,
  onInfoPress,
  meritLadderLabel,
  perTeam = false,
  weightPercent,
  onWeightPress,
  weightLocked = false,
}: Props) {
  const imageSource = resolveGameImageSource(game.image, game.name);
  const showMerits = selected && !!meritLadderLabel && typeof weightPercent === 'number';

  return (
    <Pressable
      onPress={onPress}
      disabled={locked}
      style={[
        styles.gameTile,
        selected && styles.gameTileSelected,
        locked && styles.gameTileLocked,
      ]}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected, disabled: locked }}
    >
      <Text style={styles.gameTileName} numberOfLines={1}>
        {getGameDisplayName(game)}
      </Text>
      <View style={styles.gameImageWrap}>
        <Image
          source={imageSource}
          style={[styles.gameImage, selected && styles.gameImageSelected]}
          resizeMode="cover"
        />
        {selected ? (
          <View style={styles.selectedCheckBadge}>
            <Text style={styles.selectedCheckText}>✓</Text>
          </View>
        ) : null}
        <Pressable
          style={styles.infoButton}
          onPress={onInfoPress}
          accessibilityRole="button"
          accessibilityLabel="Game info"
        >
          <Text style={styles.infoButtonText}>i</Text>
        </Pressable>
      </View>
      {showMerits ? (
        <View style={styles.meritRow}>
          <GameWeightControl
            weightPercent={weightPercent}
            onPress={onWeightPress ?? (() => {})}
            selected
            showLabel={false}
            size="compact"
            disabled={weightLocked || !onWeightPress}
            accessibilityLabel={t('selectGames.meritLadderA11y', {
              name: getGameDisplayName(game),
              ladder: meritLadderLabel,
              weight: weightPercent,
            })}
          />
          <View style={styles.meritTextWrap}>
            <Text style={styles.meritLadderText} numberOfLines={1}>
              {meritLadderLabel}
            </Text>
            <Text style={[styles.meritCaption, weightLocked && styles.meritCaptionLocked]} numberOfLines={1}>
              {weightLocked
                ? t('selectGames.meritWeightSetByTour')
                : perTeam
                  ? t('selectGames.meritsPerTeamLabel')
                  : t('selectGames.meritsLabel')}
            </Text>
          </View>
        </View>
      ) : null}
      {locked && lockedLabel ? <Text style={styles.gameTileLockedLabel}>{lockedLabel}</Text> : null}
    </Pressable>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 3px 9px rgba(0,0,0,0.2)' },
  default: {
    shadowColor: '#C8C8B0',
    shadowOpacity: 0.2,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
  },
});

const styles = StyleSheet.create({
  gameTile: {
    flex: 1,
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 8,
    minHeight: 88,
    overflow: 'hidden',
    ...cardShadow,
  },
  gameTileSelected: {
    borderColor: appColors.border.accent,
    borderWidth: 2,
  },
  gameTileLocked: {
    borderColor: '#f59e0b',
    backgroundColor: '#251603',
  },
  gameImageWrap: {
    position: 'relative',
  },
  gameImage: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 10,
  },
  infoButton: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#F2FAF1',
    borderWidth: 1,
    borderColor: appColors.border.accent,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
  },
  selectedCheckBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    paddingHorizontal: 6,
    backgroundColor: '#16a34a',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
  },
  selectedCheckText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 14,
  },
  infoButtonText: {
    color: '#0B2E1A',
    fontSize: 15,
    fontWeight: '800',
  },
  gameImageSelected: {
    opacity: 0.6,
  },
  gameTileName: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
    textAlign: 'center',
    marginBottom: 8,
  },
  gameTileLockedLabel: {
    marginTop: 4,
    fontSize: 11,
    fontWeight: '700',
    color: '#fcd34d',
    textAlign: 'center',
  },
  meritRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
  },
  meritTextWrap: {
    flexShrink: 1,
  },
  meritLadderText: {
    fontSize: 13,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  meritCaption: {
    fontSize: 11,
    color: appColors.text.muted,
  },
  meritCaptionLocked: {
    color: '#fcd34d',
  },
});
